import { transparentize } from 'polished';
import { setAlpha } from '../../../design-tokens';
import { DesignTokens, renderInlineCssVars } from '../../../theme';
import { getCheckboxThemeTokens } from '../../Checkbox/Checkbox';
import { getRadioButtonThemeTokens } from '../../RadioButton/__internal__';
import { ListDesignProps, ListTokens, ListVariant } from '../types';
import { FontStyle } from '../../../theme';
import { CSSProperties } from 'react';

export const getListTokens = (
  dt: DesignTokens,
  theme?: ListTokens['theme'],
  font?: ListTokens['font'],
  variant?: ListDesignProps['variant'],
  whiteSpace?: ListTokens['whiteSpace']
): CSSProperties => {
  let tokenValues = {
    ...getCheckboxThemeTokens(dt),
    ...getRadioButtonThemeTokens(dt),
    ...getListBaseThemeTokens(dt, variant),
    ...renderInlineCssVars(
      {
        'squish-s': dt.spacing.insetSquish.s.padding,
        s: dt.spacing.core.s,
        xs: dt.spacing.core.xs,
        xl: dt.spacing.core.xl,
      },
      'list-spacing'
    ),
    ...renderInlineCssVars(
      {
        'font-size-m': dt.enableNovaTokens ? dt.fontNova.size.m : dt.font.size.m,
        'font-size-s': dt.enableNovaTokens ? dt.fontNova.size.s : dt.font.size.s,
      },
      'list'
    ),
    ...font,
    ...(!!whiteSpace ? renderInlineCssVars({ 'white-space': whiteSpace }, 'list-item') : {}),
  };
  const overrides = theme;
  if (overrides) {
    tokenValues = {
      ...tokenValues,
      ...renderInlineCssVars(overrides({ state: 'normal' }), 'list-item'),
      ...renderInlineCssVars(overrides({ state: 'hover' }), 'list-item-hover'),
      ...renderInlineCssVars(overrides({ state: 'pressed' }), 'list-item-active'),
      ...renderInlineCssVars(overrides({ state: 'selected' }), 'list-item-selected'),
      ...renderInlineCssVars(overrides({ state: 'disabled' }), 'list-item-disabled'),
    };
  }
  return tokenValues;
};

const getListBaseThemeTokens = (dt: DesignTokens, variant: ListVariant | undefined) => {
  if (dt.enableNovaTokens) {
    let listItemFg, listItemHoverFg, listItemHoverBg, listItemFocusOutline;
    if (variant === 'primary-light' && dt.themeVariant === 'light') {
      listItemFg = dt.c.colorTextInverse;
      listItemHoverFg = dt.c.colorTextInverse;
      listItemHoverBg = setAlpha(dt.c.colorUiLight, 0.1);
      listItemFocusOutline = 'var(--focus-dark-bg)';
    } else {
      listItemFg = dt.c.colorTextDefault;
      listItemHoverFg = dt.stateStandardNova.hover.fg;
      listItemHoverBg = dt.stateStandardNova.hover.bg;
      listItemFocusOutline = 'var(--focus-light-bg)';
    }

    return {
      '--list-item-fg': listItemFg,
      '--list-item-disabled-fg': dt.stateStandardNova.disabled.fg,
      '--list-item-disabled-bg': 'transparent',
      '--list-item-secondary-fg': dt.c.colorTextLabel,
      '--list-item-hover-bg': listItemHoverBg,
      '--list-item-hover-fg': listItemHoverFg,
      '--list-item-focus-outline': listItemFocusOutline,
      '--list-item-selected-bg': dt.stateStandardNova.selected.bg,
      '--list-item-selected-fg': dt.stateStandardNova.selected.fg,
      '--list-item-selected-focus-outline':
        dt.themeVariant === 'high-contrast' ? 'var(--focus-dark-bg)' : listItemFocusOutline,
      '--list-item-active-bg': dt.stateStandardNova.active.bg,
      '--list-item-active-fg': dt.stateStandardNova.active.fg,
      '--list-item-selected-filter': dt.stateStandardNova.selected.filter,
      '--list-item-active-filter': dt.stateStandardNova.active.filter,
      '--list-item-separator-bd': dt.c.colorUiBorder,
      '--list-item-highlighted-bg': dt.text.highlighted.bg,
      '--list-item-highlighted-fg': dt.text.highlighted.fg,
    };
  }

  switch (dt.themeVariant) {
    case 'dark':
      return {
        '--list-item-fg': dt.c.white,
        '--list-item-disabled-fg': dt.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': transparentize(0.2, dt.c.white),
        '--list-item-hover-bg': transparentize(0.9, dt.c.white),
        '--list-item-hover-fg': dt.c.white,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': transparentize(0.85, dt.c.white),
        '--list-item-selected-fg': dt.c.white,
        '--list-item-active-bg': transparentize(0.85, dt.c.white),
        '--list-item-active-fg': dt.stateStandard.active.fg,
        '--list-item-selected-filter': dt.stateStandard.selected.filter,
        '--list-item-active-filter': dt.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.white15,
      };
    case 'high-contrast':
      return {
        '--list-item-fg': dt.c.white,
        '--list-item-disabled-fg': dt.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': dt.c.white,
        '--list-item-hover-bg': dt.stateStandard.hover.bg,
        '--list-item-hover-fg': dt.stateStandard.hover.fg,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': dt.stateStandard.selected.bg,
        '--list-item-selected-fg': dt.stateStandard.selected.fg,
        '--list-item-active-bg': dt.stateStandard.active.bg,
        '--list-item-active-fg': dt.stateStandard.active.fg,
        '--list-item-selected-filter': dt.stateStandard.selected.filter,
        '--list-item-active-filter': dt.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.white,
      };
    default:
      return {
        '--list-item-fg': dt.c.n_3,
        '--list-item-disabled-fg': dt.stateStandard.disabled.fg,
        '--list-item-disabled-bg': 'transparent',
        '--list-item-secondary-fg': dt.c.n_1,
        '--list-item-hover-bg': dt.stateStandard.hover.bg,
        '--list-item-hover-fg': dt.stateStandard.hover.fg,
        '--list-item-focus-outline': 'var(--focus-light-bg)',
        '--list-item-selected-bg': dt.stateStandard.selected.bg,
        '--list-item-selected-fg': dt.stateStandard.selected.fg,
        '--list-item-active-bg': dt.stateStandard.active.bg,
        '--list-item-active-fg': dt.stateStandard.active.fg,
        '--list-item-selected-filter': dt.stateStandard.selected.filter,
        '--list-item-active-filter': dt.stateStandard.active.filter,
        '--list-item-separator-bd': dt.c.n_3,
      };
  }
};

// Transform names from tokens prop to more expected CSS property names
export const transformTokensToCSS = (
  index: number,
  tokens?: (
    index?: number
  ) => {
    font?: Partial<FontStyle>;
  }
) => {
  let css: CSSProperties = {};
  if (tokens) {
    const t: { [key: string]: string | number } = {};
    Object.entries(tokens(index).font ?? {}).forEach(([k, v]) => {
      const name = k.replace(/([A-Z])/g, (match) => `-${match.toLowerCase()}`);
      if (v && name) t[name] = v;
    });
    css = renderInlineCssVars(t, 'list-item');
  }
  return css;
};
